import { Alert, Box, Skeleton } from '@mui/material';
import { SwitchableTenantHierarchyDTO } from 'probonio-shared-ui/api';
import { findTenantInHierarchy, flattenTenants } from 'probonio-shared-ui/utils/tenant';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TenantTreeNode from './TenantTreeNode';

interface Props {
  /** Tenants to surface as tree roots. Every one of them has to be reachable from `rootTenants`. */
  tenantIds: string[];
  currentTenantId: string;
  /** The forest to resolve `tenantIds` against. The caller owns loading it - this component never fetches. */
  rootTenants?: SwitchableTenantHierarchyDTO[];
  search?: string;
  /** State of the caller's search request - without it a pending or failed search is indistinguishable from zero hits. */
  isSearchLoading?: boolean;
  isSearchError?: boolean;
  selectedTenantId?: string;
  setSelectedTenantId?: (tenantId: string) => void;
}

function getPathToNode(nodes: SwitchableTenantHierarchyDTO[], targetId: string, level = 0): Record<number, string> | null {
  for (const node of nodes) {
    if (node.tenant.id === targetId) {
      return {};
    }
    if (node.children.length) {
      const childPath = getPathToNode(node.children, targetId, level + 1);
      if (childPath !== null) {
        return { [level]: node.tenant.id, ...childPath };
      }
    }
  }
  return null;
}

function getFirstPathExpanded(nodes: SwitchableTenantHierarchyDTO[], level = 0): Record<number, string> {
  const first = nodes.find(n => n.children.length);
  if (!first) {
    return {};
  }
  return { [level]: first.tenant.id, ...getFirstPathExpanded(first.children, level + 1) };
}

function isContainedWithin(node: SwitchableTenantHierarchyDTO, nodes: SwitchableTenantHierarchyDTO[]): boolean {
  return nodes.some(other => other !== node && !!findTenantInHierarchy(other, node.tenant.id));
}

function mergeExpandedByLevel(base: Record<number, string>, override: Record<number, string>): Record<number, string> {
  const result = { ...base };
  for (const [levelStr, tenantId] of Object.entries(override)) {
    const level = Number(levelStr);
    if (result[level] !== tenantId) {
      Object.keys(result).forEach(k => {
        const numKey = Number(k);
        if (numKey >= level) delete result[numKey];
      });
    }
    result[level] = tenantId;
  }
  return result;
}

function toExpandedIds(levelMap: Record<number, string>, collapsedLevel: Set<string>): Record<string, boolean> {
  return Object.fromEntries(
    Object.values(levelMap)
      .filter(id => !collapsedLevel.has(id))
      .map(id => [id, true]),
  );
}

export default function TenantTree({
  tenantIds,
  currentTenantId,
  rootTenants,
  search = '',
  isSearchLoading = false,
  isSearchError = false,
  selectedTenantId: propSelectedTenantId,
  setSelectedTenantId: propSetSelectedTenantId,
}: Props) {
  const { t } = useTranslation('common');
  const { t: tError } = useTranslation('errorModule');
  const [expandedLevel, setExpandedLevel] = useState<Record<number, string>>({});
  const [collapsedLevel, setCollapsedLevel] = useState<Set<string>>(new Set());
  const initializedSearchResetRef = useRef(false);
  const [internalSelectedTenantId, setInternalSelectedTenantId] = useState<string>(currentTenantId);

  const selectedTenantId = propSelectedTenantId ?? internalSelectedTenantId;
  const setSelectedTenantId = propSetSelectedTenantId ?? setInternalSelectedTenantId;
  const normalizedQuery = search.toLowerCase();
  const isSearchMode = normalizedQuery.length > 0;

  // Note there is no client-side name filter in search mode: the caller passes exactly the hits the API returned,
  // and the API also matches on mailDomain - re-filtering on the name would drop every domain hit as "no results".
  const hierarchies = useMemo(() => {
    const allNodes = new Map((rootTenants ?? []).flatMap(flattenTenants).map(n => [n.tenant.id, n]));

    const found = tenantIds.reduce<SwitchableTenantHierarchyDTO[]>((acc, id) => {
      const node = allNodes.get(id);
      if (node) acc.push(node);
      return acc;
    }, []);

    return found.filter(node => !isContainedWithin(node, found));
  }, [rootTenants, tenantIds]);

  useEffect(() => {
    if (isSearchMode || hierarchies.length === 0 || !selectedTenantId) {
      return;
    }
    const path = getPathToNode(hierarchies, selectedTenantId);
    setExpandedLevel(path ?? {});
    setCollapsedLevel(new Set());
  }, [isSearchMode, hierarchies, selectedTenantId]);

  useEffect(() => {
    if (!initializedSearchResetRef.current) {
      initializedSearchResetRef.current = true;
      return;
    }
    setExpandedLevel({});
    setCollapsedLevel(new Set());
  }, [normalizedQuery]);

  const firstPathExpanded = isSearchMode ? getFirstPathExpanded(hierarchies) : {};
  const levelMap = isSearchMode ? mergeExpandedByLevel(firstPathExpanded, expandedLevel) : expandedLevel;
  const expandedIds = toExpandedIds(levelMap, collapsedLevel);

  const onToggleExpand = (level: number, tenantId: string) => {
    if (expandedIds[tenantId]) {
      setCollapsedLevel(prev => new Set([...prev, tenantId]));
      setExpandedLevel(prev => {
        const next = { ...prev };
        if (next[level] === tenantId) delete next[level];
        return next;
      });
    } else {
      setCollapsedLevel(prev => {
        const next = new Set(prev);
        next.delete(tenantId);
        return next;
      });
      setExpandedLevel(prev => {
        const next = Object.fromEntries(Object.entries(prev).filter(([l]) => Number(l) < level));
        next[level] = tenantId;
        return next;
      });
    }
  };

  // An empty forest in search mode only means "nothing matched" once the request actually came back: while it is
  // in flight or after it failed there is nothing to report yet.
  const searchFailed = isSearchMode && isSearchError;
  const searchPending = isSearchMode && !isSearchError && isSearchLoading;
  const searchEmpty = isSearchMode && !isSearchError && !isSearchLoading && hierarchies.length === 0;

  return (
    <Box>
      {searchFailed && <Alert severity="error">{tError('defaultMessage')}</Alert>}
      {searchPending && <Skeleton variant="rounded" height={40} />}
      {searchEmpty && <Alert severity="warning">{t('noResults')}</Alert>}
      {hierarchies.map(root => (
        <TenantTreeNode
          key={root.tenant.id}
          hierarchyNode={root}
          currentTenantId={currentTenantId}
          selectedTenantId={selectedTenantId}
          expanded={expandedIds}
          onTenantSelect={setSelectedTenantId}
          onToggleExpand={onToggleExpand}
        />
      ))}
    </Box>
  );
}
